import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Wallet, TrendingUp, Users, ChevronDown, ChevronUp, Award, ArrowDownRight, Lock, Unlock, Target } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const Commissions = () => {
  const { user } = useAuth();
  const [earnings, setEarnings] = useState(null);
  const [history, setHistory] = useState([]);
  const [teamEarnings, setTeamEarnings] = useState(null);
  const [rates, setRates] = useState([]);
  const [levelStatus, setLevelStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRates, setShowRates] = useState(true);

  useEffect(() => {
    fetchCommissionData();
  }, []);

  const fetchCommissionData = async () => {
    try {
      setLoading(true);
      const [earningsRes, historyRes, teamRes, ratesRes, levelRes] = await Promise.all([
        api.get('/commission/my-earnings'),
        api.get('/commission/history?limit=50'),
        api.get('/commission/team-earnings'),
        api.get('/commission/rates'),
        api.get('/commission/my-level-status')
      ]);
      setEarnings(earningsRes.data);
      setHistory(historyRes.data);
      setTeamEarnings(teamRes.data);
      setRates(ratesRes.data.rates);
      setLevelStatus(levelRes.data);
    } catch (error) {
      console.error('Failed to fetch commission data:', error);
      toast.error('Failed to load commission data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-xl">Loading commission data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="commissions-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold mb-2">My Commissions</h1>
        <p className="text-muted-foreground mb-8">Track your Affiliate Network earnings and team performance</p>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* Commission Wallet Balance */}
          <div className="glass-card p-6 border-2 border-accent/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center">
                <Wallet className="w-6 h-6 text-accent" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Commission Wallet</h3>
                <p className="text-xs text-muted-foreground">Current Balance</p>
              </div>
            </div>
            <p className="text-4xl font-bold text-accent" data-testid="commission-balance">
              ₹{earnings?.wallet_balance?.toFixed(2) || '0.00'}
            </p>
          </div>

          {/* Total Earned */}
          <div className="glass-card p-6 border-2 border-green-500/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Total Earned</h3>
                <p className="text-xs text-muted-foreground">All time earnings</p>
              </div>
            </div>
            <p className="text-4xl font-bold text-green-500" data-testid="total-earned">
              ₹{earnings?.total_earned?.toFixed(2) || '0.00'}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {earnings?.total_transactions || 0} transactions
            </p>
          </div>

          {/* Direct Referrals */}
          <div className="glass-card p-6 border-2 border-blue-500/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Direct Referrals</h3>
                <p className="text-xs text-muted-foreground">Your team members</p>
              </div>
            </div>
            <p className="text-4xl font-bold text-blue-500" data-testid="team-count">
              {levelStatus?.direct_referrals || 0}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {teamEarnings?.team?.filter(t => t.is_activated).length || 0} activated
            </p>
          </div>

          {/* Unlocked Level */}
          <div className="glass-card p-6 border-2 border-purple-500/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Unlocked Level</h3>
                <p className="text-xs text-muted-foreground">Commission depth</p>
              </div>
            </div>
            <p className="text-4xl font-bold text-purple-500" data-testid="unlocked-level">
              L{levelStatus?.unlocked_level || 0}
            </p>
            {levelStatus?.next_level && (
              <p className="text-sm text-muted-foreground mt-2">
                Need {levelStatus.directs_needed_for_next} more for L{levelStatus.next_level}
              </p>
            )}
          </div>
        </div>

        {/* Level Unlock Progress */}
        <div className="glass-card p-6 mb-8" data-testid="level-unlock-section">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-purple-500" />
            Level Unlock Status
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Unlock levels by getting direct referrals. You currently have <strong className="text-blue-500">{levelStatus?.direct_referrals || 0}</strong> direct referrals.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {levelStatus?.levels?.map(level => (
              <div 
                key={level.level}
                className={`rounded-xl p-4 text-center transition-all ${
                  level.is_unlocked 
                    ? 'bg-gradient-to-br from-green-500/20 to-emerald-600/20 border-2 border-green-500/50' 
                    : 'bg-white/5 border border-white/10 opacity-60'
                }`}
                data-testid={`level-status-${level.level}`}
              >
                <div className="flex justify-center mb-2">
                  {level.is_unlocked ? (
                    <Unlock className="w-5 h-5 text-green-500" />
                  ) : (
                    <Lock className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Level {level.level}</p>
                <p className={`text-2xl font-bold ${level.is_unlocked ? 'text-green-500' : 'text-muted-foreground'}`}>
                  {level.rate}%
                </p>
                <p className={`text-xs mt-1 ${level.is_unlocked ? 'text-green-400' : 'text-muted-foreground'}`}>
                  {level.directs_required} direct{level.directs_required > 1 ? 's' : ''}
                </p>
              </div>
            ))}
          </div>

          {/* Progress Bar */}
          {levelStatus && levelStatus.unlocked_level < 7 && (
            <div className="mt-6">
              <div className="flex justify-between text-sm mb-2">
                <span>Progress to Level {levelStatus.next_level}</span>
                <span className="text-primary font-bold">
                  {levelStatus.direct_referrals} / {levelStatus.levels?.find(l => l.level === levelStatus.next_level)?.directs_required || 0}
                </span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-primary to-accent h-3 rounded-full transition-all"
                  style={{ 
                    width: `${Math.min(100, (levelStatus.direct_referrals / (levelStatus.levels?.find(l => l.level === levelStatus.next_level)?.directs_required || 1)) * 100)}%` 
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Level Breakdown - Earnings */}
        {earnings?.level_breakdown && Object.keys(earnings.level_breakdown).length > 0 && (
          <div className="glass-card p-6 mb-8" data-testid="level-breakdown">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-primary" />
              Earnings by Level
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {[1, 2, 3, 4, 5, 6, 7].map(level => {
                const levelData = earnings.level_breakdown[`level_${level}`];
                const isUnlocked = levelStatus?.levels?.find(l => l.level === level)?.is_unlocked;
                return (
                  <div key={level} className={`rounded-xl p-4 text-center ${isUnlocked ? 'bg-white/5' : 'bg-white/5 opacity-50'}`}>
                    <p className="text-xs text-muted-foreground mb-1">Level {level}</p>
                    <p className="text-lg font-bold text-primary">
                      ₹{levelData?.total?.toFixed(2) || '0.00'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {levelData?.count || 0} txns
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Commission Rates Info */}
        <div className="glass-card p-6 mb-8">
          <button
            onClick={() => setShowRates(!showRates)}
            className="w-full flex items-center justify-between"
            data-testid="toggle-rates-btn"
          >
            <h2 className="text-xl font-bold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              Commission Rules & Requirements
            </h2>
            {showRates ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showRates && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Rates Table */}
                <div>
                  <h3 className="font-semibold mb-3">Commission Rates</h3>
                  <div className="bg-white/5 rounded-xl overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-white/10">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm">Level</th>
                          <th className="px-4 py-2 text-center text-sm">Rate</th>
                          <th className="px-4 py-2 text-center text-sm">Direct Required</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rates.map(rate => (
                          <tr key={rate.level} className="border-t border-white/5">
                            <td className="px-4 py-2 text-sm">Level {rate.level}</td>
                            <td className="px-4 py-2 text-center text-sm font-bold text-primary">{rate.rate}%</td>
                            <td className="px-4 py-2 text-center text-sm">{rate.directs_required} direct{rate.directs_required > 1 ? 's' : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Rules */}
                <div>
                  <h3 className="font-semibold mb-3">Important Rules</h3>
                  <div className="space-y-3">
                    <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                      <p className="text-sm text-green-500">
                        <strong>Activation Required:</strong> Only activated members receive commissions. Activate now to start earning!
                      </p>
                    </div>
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                      <p className="text-sm text-blue-500">
                        <strong>Direct Referrals:</strong> Each level unlocks with more direct referrals. Get 10 directs to unlock all 7 levels!
                      </p>
                    </div>
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                      <p className="text-sm text-yellow-500">
                        <strong>Skipped Sponsors:</strong> If a sponsor in your upline is not activated or hasn't unlocked that level, they are skipped.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Commission History */}
        <div className="glass-card p-6" data-testid="commission-history">
          <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
            <ArrowDownRight className="w-5 h-5 text-green-500" />
            Commission History
          </h2>
          
          {history.length === 0 ? (
            <div className="text-center py-12">
              <Wallet className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-xl font-semibold mb-2">No Commissions Yet</p>
              <p className="text-muted-foreground">
                Start referring activated members to earn commissions on their purchases!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((item, index) => (
                <div 
                  key={item.id || index}
                  className="bg-white/5 rounded-xl p-4 flex items-center justify-between hover:bg-white/10 transition-all"
                  data-testid={`commission-item-${index}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      item.level === 1 ? 'bg-green-500/20' :
                      item.level === 2 ? 'bg-blue-500/20' :
                      item.level === 3 ? 'bg-purple-500/20' :
                      'bg-primary/20'
                    }`}>
                      <span className={`text-lg font-bold ${
                        item.level === 1 ? 'text-green-500' :
                        item.level === 2 ? 'text-blue-500' :
                        item.level === 3 ? 'text-purple-500' :
                        'text-primary'
                      }`}>
                        L{item.level}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold">{item.purchase_name}</p>
                      <p className="text-sm text-muted-foreground">
                        From: {item.purchaser_name} • {item.purchase_type}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(item.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-green-500">
                      +₹{item.commission_amount?.toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {item.commission_rate}% of ₹{item.purchase_amount?.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Team Section */}
        {teamEarnings && teamEarnings.team?.length > 0 && (
          <div className="glass-card p-6 mt-8" data-testid="team-section">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              Your Direct Team ({teamEarnings.total_team_members})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teamEarnings.team.map((member, index) => (
                <div 
                  key={index}
                  className={`bg-white/5 rounded-xl p-4 border ${
                    member.is_activated ? 'border-green-500/30' : 'border-red-500/30'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold">{member.name}</p>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      member.is_activated 
                        ? 'bg-green-500/20 text-green-500' 
                        : 'bg-red-500/20 text-red-500'
                    }`}>
                      {member.is_activated ? 'Activated' : 'Free'}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">@{member.username}</p>
                  <p className="text-sm">
                    Total Purchases: <span className="font-bold text-primary">₹{member.total_purchases?.toFixed(2) || '0.00'}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Commissions;
